'use client'

import { pageEvent, trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { SCROLL_EVENT } from '@components/Scroll/scrollConstants'
import AnalyticsViewPage, { waitForAnalyticsLoad } from '@components/utils/analyticsViewPage'
import { TAP_BTN_ENGAGE } from '@constants/homeSegmentEvent'
import { segmentEventCommonPropFunc } from '@utils/segmentProperties'
import { useEffect } from 'react'
import { Lob } from 'src/enums/Lob'
import { ImpactPremiumSectionClient } from '../InteractiveSections'
import { COMP_STICKY_NAV_ITEMS } from './data'
import { COMPREHENSIVE_HERO_CONTENT } from './heroConfig'

const segmentEventCommonProperties = {
  productWidgetData: {
    Content: {
      data: {
        attributes: {
          widget: 'bike-seo-widget',
          productWidgetMainHeading: COMPREHENSIVE_HERO_CONTENT.productWidgetMainHeading,
        },
      },
    },
  },
}

const comprehensiveEventProperties = {
  product: 'bike',
  variant_name: 'aug_2026_version',
  page_type: 'seo',
}

type EngagementComponentName =
  | 'fixed_widget'
  | 'floating_widget'
  | 'customer_review_page_redirection'
  | 'FAQ'
  | 'whats_covered'
  | 'whats_not_covered'
  | 'premium_factors'
  | 'coverage_tab'
  | 'available_add_ons'
  | 'plan_recommendation'
  | 'plan_comparison'
  | 'guide_redirection'

export const trackComprehensiveEngagement = async (
  componentName: EngagementComponentName,
  ctaText: string,
  redirectionUrl?: string
) => {
  try {
    const [commonProperties] = await Promise.all([
      segmentEventCommonPropFunc(segmentEventCommonProperties),
      waitForAnalyticsLoad(),
    ])

    await trackEvent(TAP_BTN_ENGAGE, {
      ...commonProperties,
      ...comprehensiveEventProperties,
      component_name: componentName,
      cta_text: ctaText,
      ...(redirectionUrl && { redirection_url: redirectionUrl }),
      funnel: 'engage',
    })
  } catch {
    // Analytics should not block the interaction.
  }
}

export const ComprehensiveImpactPremiumSectionClient = () => (
  <ImpactPremiumSectionClient
    onItemExpand={title => {
      void trackComprehensiveEngagement('premium_factors', title)
    }}
  />
)

export const ComprehensiveAnalyticsClient = ({ journeyData = [] }: { journeyData?: any[] }) => {
  useEffect(() => {
    const trackedSectionIds = new Set<string>()
    const sectionLabels = new Map(COMP_STICKY_NAV_ITEMS.map(item => [item.id, item.label]))
    const commonPropertiesPromise = segmentEventCommonPropFunc(segmentEventCommonProperties)
    const analyticsReadyPromise = waitForAnalyticsLoad()
    let isActive = true

    const trackSectionView = async (sectionId: string, componentName: string) => {
      try {
        const [commonProperties] = await Promise.all([commonPropertiesPromise, analyticsReadyPromise])
        if (!isActive) return

        await pageEvent(SCROLL_EVENT, {
          ...commonProperties,
          page_type: 'seo',
          component_name: componentName,
        })
      } catch {
        trackedSectionIds.delete(sectionId)
      }
    }

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const sectionId = entry.target.id
          const componentName = sectionLabels.get(sectionId)

          if (!entry.isIntersecting || !componentName || trackedSectionIds.has(sectionId)) return

          trackedSectionIds.add(sectionId)
          void trackSectionView(sectionId, componentName)
        })
      },
      { rootMargin: '-40% 0px -40% 0px' }
    )

    COMP_STICKY_NAV_ITEMS.forEach(item => {
      const section = document.getElementById(item.id)
      if (section) observer.observe(section)
    })

    return () => {
      isActive = false
      observer.disconnect()
    }
  }, [])

  return (
    <AnalyticsViewPage
      segmentEventCommonProperties={segmentEventCommonProperties}
      additionalEventProperties={comprehensiveEventProperties}
      lob={Lob.BIKE}
      widget="bike-seo-widget"
      JourneyData={journeyData}
    />
  )
}
